import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  changePasswordSchema,
  updateProfileSchema,
  type ChangePasswordInput,
  type UpdateProfileInput,
} from '@mygmao/shared';
import {
  Bell,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Upload,
  UserCog,
  KeyRound,
  Monitor,
  Image as ImageIcon,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications, type NotificationPreferences } from '../contexts/NotificationsContext';

/**
 * Page /profil — US-AUTH-04.
 *
 * Sections :
 *  - Identité (prénom, nom — email read-only, rôle read-only)
 *  - Mot de passe (current + new + confirm — révoque les autres sessions)
 *  - Avatar (upload JPG/PNG/WebP max 2 Mo + delete)
 *  - Sessions actives (liste + révocation)
 */

interface MeProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: string | null;
  role_id: string | null;
  is_platform_admin: boolean;
  must_change_password: boolean;
  avatar_url: string | null;
  avatar_signed_url: string | null;
  created_at: string;
  last_login_at: string | null;
}

interface SessionRow {
  id: string;
  jti: string;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  is_current: boolean;
}

export function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    try {
      const [me, sess] = await Promise.all([
        api.get<{ data: MeProfile }>('/me'),
        api.get<{ data: SessionRow[] }>('/me/sessions'),
      ]);
      setProfile(me.data.data);
      setSessions(sess.data.data);
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'Impossible de charger le profil'));
    }
  }

  if (loadError) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md mx-auto p-8 text-center">
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p className="text-sm text-red-700">{loadError}</p>
      </div>
    );
  }
  if (!profile || !user) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <UserCog className="w-7 h-7 mr-2 text-primary-600" />
        Mon profil
      </h1>

      <IdentitySection profile={profile} onUpdated={refresh} />
      <AvatarSection profile={profile} onUpdated={refresh} />
      <PasswordSection />
      <NotificationPreferencesSection />
      <SessionsSection sessions={sessions} onRefresh={refresh} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function IdentitySection({ profile, onUpdated }: { profile: MeProfile; onUpdated: () => void }) {
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: { first_name: profile.first_name, last_name: profile.last_name },
  });

  async function onSubmit(values: UpdateProfileInput) {
    setErrorMessage(null);
    try {
      await api.patch('/me', values);
      setStatus('saved');
      onUpdated();
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      setStatus('error');
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <Card title="Identité" icon={UserCog}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Prénom" error={errors.first_name?.message}>
            <input type="text" className={inputClass} {...register('first_name')} />
          </FormField>
          <FormField label="Nom" error={errors.last_name?.message}>
            <input type="text" className={inputClass} {...register('last_name')} />
          </FormField>
        </div>
        <FormField
          label="Email (non modifiable)"
          hint="Contacte un administrateur pour modifier l'email."
        >
          <input type="email" className={inputClass} value={profile.email} disabled />
        </FormField>

        {status === 'saved' && <FeedbackBanner kind="success" text="Profil mis à jour." />}
        {status === 'error' && errorMessage && <FeedbackBanner kind="error" text={errorMessage} />}

        <button type="submit" disabled={isSubmitting} className={btnPrimary}>
          {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Enregistrer
        </button>
      </form>
    </Card>
  );
}

function AvatarSection({ profile, onUpdated }: { profile: MeProfile; onUpdated: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('avatar', file);
      await api.post('/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdated();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDelete() {
    setUploading(true);
    setErrorMessage(null);
    try {
      await api.delete('/me/avatar');
      onUpdated();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Card title="Photo de profil" icon={ImageIcon}>
      <div className="flex items-center space-x-4">
        <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center overflow-hidden border border-gray-200">
          {profile.avatar_signed_url ? (
            <img
              src={profile.avatar_signed_url}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <UserCog className="w-8 h-8 text-primary-400" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className={`${btnOutline} cursor-pointer`}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Upload…' : 'Changer la photo'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFile}
              disabled={uploading}
            />
          </label>
          {profile.avatar_url && (
            <button type="button" onClick={handleDelete} disabled={uploading} className={btnDanger}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </button>
          )}
          <p className="text-xs text-gray-400">JPG, PNG ou WebP — 2 Mo max</p>
          {errorMessage && <FeedbackBanner kind="error" text={errorMessage} />}
        </div>
      </div>
    </Card>
  );
}

function PasswordSection() {
  const [status, setStatus] = useState<'idle' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
  });

  async function onSubmit(values: ChangePasswordInput) {
    setErrorMessage(null);
    try {
      await api.patch('/me/password', values);
      setStatus('done');
      reset();
      setTimeout(() => setStatus('idle'), 3000);
    } catch (err) {
      setStatus('error');
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <Card title="Mot de passe" icon={KeyRound}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <FormField label="Mot de passe actuel" error={errors.current_password?.message}>
          <input
            type="password"
            autoComplete="current-password"
            className={inputClass}
            {...register('current_password')}
          />
        </FormField>
        <FormField
          label="Nouveau mot de passe"
          hint="8+ caractères, majuscule, minuscule, chiffre, caractère spécial"
          error={errors.new_password?.message}
        >
          <input
            type="password"
            autoComplete="new-password"
            className={inputClass}
            {...register('new_password')}
          />
        </FormField>
        <FormField
          label="Confirmer le nouveau mot de passe"
          error={errors.new_password_confirm?.message}
        >
          <input
            type="password"
            autoComplete="new-password"
            className={inputClass}
            {...register('new_password_confirm')}
          />
        </FormField>

        {status === 'done' && (
          <FeedbackBanner
            kind="success"
            text="Mot de passe mis à jour — vos autres sessions ont été déconnectées."
          />
        )}
        {status === 'error' && errorMessage && <FeedbackBanner kind="error" text={errorMessage} />}

        <button type="submit" disabled={isSubmitting} className={btnPrimary}>
          {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Changer le mot de passe
        </button>
      </form>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Préférences notifications (US-NOTIF-05)
// ─────────────────────────────────────────────────────────────────────────────

function NotificationPreferencesSection() {
  const { preferences, updatePreferences, browserPermission, requestBrowserPermission } =
    useNotifications();
  const [status, setStatus] = useState<'idle' | 'saved'>('idle');

  if (!preferences) {
    return (
      <Card title="Notifications" icon={Bell}>
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </Card>
    );
  }

  async function toggle(key: keyof NotificationPreferences, value: boolean | string) {
    await updatePreferences({ [key]: value } as Partial<NotificationPreferences>);
    setStatus('saved');
    setTimeout(() => setStatus('idle'), 1500);
  }

  return (
    <Card title="Notifications" icon={Bell}>
      <div className="space-y-3 text-sm">
        <Toggle
          label="Son à la réception d'une notification"
          checked={preferences.sound_enabled}
          onChange={(v) => toggle('sound_enabled', v)}
        />
        <Toggle
          label="Notifications navigateur (push)"
          checked={preferences.browser_push_enabled}
          onChange={(v) => toggle('browser_push_enabled', v)}
          hint={
            browserPermission === 'denied'
              ? 'Autorisation refusée par votre navigateur — réactivez-la dans ses paramètres de site.'
              : browserPermission === 'default'
                ? 'Autorisation pas encore demandée.'
                : 'Autorisation accordée.'
          }
          extra={
            preferences.browser_push_enabled && browserPermission === 'default' ? (
              <button
                type="button"
                onClick={() => requestBrowserPermission()}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Demander l'autorisation
              </button>
            ) : null
          }
        />

        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Doublon par email
          </p>
          <div className="space-y-2">
            <Toggle
              label="Notifications urgentes"
              checked={preferences.email_for_urgent}
              onChange={(v) => toggle('email_for_urgent', v)}
            />
            <Toggle
              label="Notifications priorité haute"
              checked={preferences.email_for_high}
              onChange={(v) => toggle('email_for_high', v)}
            />
            <Toggle
              label="Notifications normales"
              checked={preferences.email_for_normal}
              onChange={(v) => toggle('email_for_normal', v)}
            />
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
            Récapitulatif périodique
          </label>
          <select
            value={preferences.digest_frequency}
            onChange={(e) => toggle('digest_frequency', e.target.value)}
            className={inputClass + ' max-w-xs'}
          >
            <option value="never">Jamais</option>
            <option value="daily">Quotidien</option>
            <option value="weekly">Hebdomadaire</option>
          </select>
        </div>

        {status === 'saved' && (
          <p className="text-xs text-green-600 inline-flex items-center">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Préférences enregistrées
          </p>
        )}
      </div>
    </Card>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
  extra,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
  hint?: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">{label}</span>
        </label>
        {hint && <p className="text-xs text-gray-400 mt-0.5 ml-6">{hint}</p>}
      </div>
      {extra}
    </div>
  );
}

function SessionsSection({
  sessions,
  onRefresh,
}: {
  sessions: SessionRow[];
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function revoke(id: string) {
    setBusy(id);
    try {
      await api.delete(`/me/sessions/${id}`);
      onRefresh();
    } finally {
      setBusy(null);
    }
  }

  async function revokeAll() {
    setBusy('all');
    try {
      await api.delete('/me/sessions');
      onRefresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title={`Sessions actives (${sessions.length})`} icon={Monitor}>
      <div className="space-y-2">
        {sessions.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
          >
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {s.user_agent ?? 'Agent inconnu'}{' '}
                {s.is_current && <span className="text-xs text-primary-600">• cette session</span>}
              </p>
              <p className="text-xs text-gray-500">
                IP {s.ip ?? '?'} · dernière activité {new Date(s.last_seen_at).toLocaleString()}
              </p>
            </div>
            {!s.is_current && (
              <button onClick={() => revoke(s.id)} disabled={busy === s.id} className={btnDanger}>
                {busy === s.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        ))}
        {sessions.length > 1 && (
          <button onClick={revokeAll} disabled={busy === 'all'} className={btnOutline}>
            {busy === 'all' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Déconnecter toutes les autres sessions
          </button>
        )}
      </div>
    </Card>
  );
}

// ─── UI helpers ──────────────────────────────────────────────────────────────

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm disabled:bg-gray-50 disabled:text-gray-500';

const btnPrimary =
  'inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors disabled:opacity-50';

const btnOutline =
  'inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50';

const btnDanger =
  'inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50';

function Card({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Icon className="w-5 h-5 mr-2 text-primary-600" />
        {title}
      </h2>
      {children}
    </div>
  );
}

function FormField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {children}
      {error ? (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      ) : hint ? (
        <p className="text-gray-400 text-xs mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function FeedbackBanner({ kind, text }: { kind: 'success' | 'error'; text: string }) {
  const cls =
    kind === 'success'
      ? 'border-green-200 bg-green-50 text-green-700'
      : 'border-red-200 bg-red-50 text-red-700';
  const Icon = kind === 'success' ? CheckCircle2 : AlertCircle;
  return (
    <div className={`rounded-lg border p-3 flex items-start space-x-2 ${cls}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
